import { useEffect, useState, useCallback } from "react";
import { useI18n } from "../../lib/i18n.jsx";
import { GALLERY_IMAGES } from "../../lib/content.js";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Reveal from "../Reveal.jsx";

export default function Gallery() {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);

  const openAt = (i) => {
    setIndex(i);
    setOpen(true);
  };

  const next = useCallback(
    () => setIndex((i) => (i + 1) % GALLERY_IMAGES.length),
    []
  );
  const prev = useCallback(
    () => setIndex((i) => (i - 1 + GALLERY_IMAGES.length) % GALLERY_IMAGES.length),
    []
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, next, prev]);

  const active = GALLERY_IMAGES[index];

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="bg-cream py-20 md:py-28"
    >
      <div className="max-w-[1300px] mx-auto px-5 md:px-8">
        <Reveal as="div" className="text-center max-w-2xl mx-auto mb-12">
          <p className="font-handwritten text-summer text-2xl md:text-3xl mb-2">
            {t.facility.kicker}
          </p>
          <h2
            className="font-display font-bold text-winter leading-tight mb-4"
            style={{ fontSize: "clamp(2rem, 4.5vw, 3.5rem)" }}
          >
            {t.facility.title}
          </h2>
          <p className="text-lg text-winter/65">{t.facility.subtitle}</p>
        </Reveal>

        {/* Featured 2-card facility */}
        <div className="grid md:grid-cols-2 gap-5 mb-5">
          {[
            { src: "/img/skatepark.webp", title: t.facility.skatepark_title, body: t.facility.skatepark_body, testid: "facility-skatepark" },
            { src: "/img/nocleg.webp", title: t.facility.hostel_title, body: t.facility.hostel_body, testid: "facility-hostel" }
          ].map((f, i) => (
            <div key={i} className="card-soft overflow-hidden cursor-pointer" onClick={() => openAt(GALLERY_IMAGES.findIndex(g => g.src === f.src))} data-testid={f.testid}>
              <div className="relative aspect-[16/10] overflow-hidden">
                <img src={f.src} alt={f.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 hover:scale-110" />
              </div>
              <div className="p-6">
                <h3 className="font-display font-bold text-winter text-xl md:text-2xl mb-2">{f.title}</h3>
                <p className="text-winter/65">{f.body}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Gallery grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {GALLERY_IMAGES.map((img, i) => (
            <button
              key={i}
              onClick={() => openAt(i)}
              data-testid={`gallery-image-${i}`}
              className="relative aspect-square overflow-hidden rounded-2xl group cursor-pointer"
            >
              <img
                src={img.src}
                alt={img.alt}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
            </button>
          ))}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="gallery-dialog"
          className="bg-winter border-winter/20 max-w-5xl p-0 overflow-hidden rounded-2xl [&>button]:text-cream [&>button]:bg-cream/10 [&>button]:hover:bg-cream/20 [&>button]:rounded-full [&>button]:w-10 [&>button]:h-10 [&>button]:top-4 [&>button]:right-4 [&>button]:opacity-100 [&>button]:ring-0 [&>button]:flex [&>button]:items-center [&>button]:justify-center"
        >
          <DialogTitle className="sr-only">{active?.alt || "Gallery image"}</DialogTitle>
          <DialogDescription className="sr-only">Scootive Camp gallery photo {index + 1} of {GALLERY_IMAGES.length}</DialogDescription>
          <div className="relative">
            <img
              src={active.src}
              alt={active.alt}
              className="w-full h-auto max-h-[85vh] object-contain bg-winter"
              data-testid="gallery-active-image"
            />

            {/* Nav arrows */}
            <button
              onClick={prev}
              data-testid="gallery-prev"
              aria-label="Previous"
              className="absolute left-3 top-1/2 -translate-y-1/2 bg-cream/90 hover:bg-summer hover:text-cream text-winter w-11 h-11 rounded-full flex items-center justify-center transition-colors shadow-lg"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={next}
              data-testid="gallery-next"
              aria-label="Next"
              className="absolute right-3 top-1/2 -translate-y-1/2 bg-cream/90 hover:bg-summer hover:text-cream text-winter w-11 h-11 rounded-full flex items-center justify-center transition-colors shadow-lg"
            >
              <ChevronRight className="w-5 h-5" />
            </button>

            {/* Counter */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-winter/80 text-cream text-xs font-mono px-3 py-1.5 rounded-full">
              {index + 1} / {GALLERY_IMAGES.length}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
